import { Box } from "@modernatx-internal/ui-kit-react";
import React from "react";

import { useCheckForm } from "@/hooks/useCheckForm";
import { useExperience } from "@/hooks/useExperience";
import { Scenario } from "@/types";

interface PageAppStateProps {
  scenario?: Scenario | null;
}

const propertyDisplayName = (property?: string | null): string | undefined => {
  if (!property) {
    return undefined;
  }

  return property
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

export const PageAppState: React.FC<PageAppStateProps> = ({ scenario }) => {
  const { cookieCountry, documentIds, pathCountry, pathLanguage } = useExperience();
  const { hours, minutes, packaging, product, storage, temperature, unit } = useCheckForm();
  const appState = React.useMemo(() => {
    const searchParams = new URLSearchParams();

    if (cookieCountry) {
      searchParams.set("cookieCountryCode", cookieCountry.iso2);
    }

    if (pathCountry) {
      searchParams.set("pathCountryCode", pathCountry.iso2);
    }

    if (pathLanguage) {
      searchParams.set("pathLanguageCode", pathLanguage.iso2);
    }

    if (scenario) {
      searchParams.set("scenarioCountryCode", scenario.countryCode);
      searchParams.set("scenarioDocumentId", String(scenario.documentId));
      searchParams.set("scenarioId", scenario.scenarioId);
    }

    return searchParams.toString();
  }, [cookieCountry, pathCountry, pathLanguage, scenario]);
  const temperatureUnit = React.useMemo(() => {
    if (unit === "CELSIUS") {
      return "Celsius";
    } else if (unit === "FAHRENHEIT") {
      return "Fahrenheit";
    }
  }, [unit]);

  return (
    <Box
      data-track-app-state={appState}
      data-track-app-version={`${process.env.version}|${documentIds.join(",")}`}
      data-track-hours={hours || 0}
      data-track-minutes={minutes || 0}
      data-track-packaging={packaging}
      data-track-product={propertyDisplayName(product)?.replace(/mrna|m rna/i, "mRNA")}
      data-track-storage={propertyDisplayName(storage)}
      data-track-temperature-unit={temperatureUnit}
      data-track-temperature={temperature}
      id="track-app-state"
    />
  );
};
